import React, { FormEvent, useMemo, useState } from "react"

import { Box, List, Typography } from "@mui/material"
import { ErrorMessage, Form, Formik } from "formik"
import { LocalizedEnum, LocalizedStrict } from "../../localization/components/AppLocalized"
import { tss } from "tss-react/mui"
import { selectCompanies, selectCompanyDuplicateValue, selectCompanyReason, selectUsername } from "../loginSelectors"
import { useSelector } from "react-redux"
import { groupBy } from "lodash-es"
import { CompanyIdChoice, SelectCompanyReason } from "../loginTypes"
import { FormikRadioListItem, FormikSubmitButton } from "../../../shared/components/forms/Formik"
import * as yup from "yup"
import { usePublicApiMutation } from "../../../core/api/useApiMutation"
import { selectCompany } from "../loginApi"
import { useLoginFlow } from "../../../shared/hooks/useLoginFlow"
import { useAppLocalization } from "../../localization/components/AppLocalizationProvider"

const useStyles = tss.create(({ theme }) => ({
    duplicateValue: {
        color: "#3c8530", // no tailwind. color copied from mobile
        fontWeight: theme.typography.fontWeightBold,
    },
    companyList: {
        paddingTop: theme.spacing(3),
    },
    companiesWrapper: {
        marginBottom: theme.spacing(6),
    },
    header: {
        textAlign: "left",
        fontWeight: 800,
        color: theme.palette.grey[800],
        paddingBottom: theme.spacing(3),
    },
    formPara: {
        textAlign: "left",
        fontSize: "1.1rem",
        color: theme.palette.grey[900],
        margin: 0,
    },
    errorMessage: {
        marginBottom: theme.spacing(2),
        color: theme.palette.tailwind.red[600],
    },
    submitButton: {
        marginTop: "2rem",
        marginBottom: 2,
    },
}))

type SelectCompanyMode = "explanation" | "list"

type FormValues = {
    selectedCompany: string
}

const initialValues: FormValues = {
    selectedCompany: "",
}

type ForgotPasswordProps = Readonly<{
    isDefault?: boolean
    isActivateDa?: boolean
}>

export function SelectCompany({ isDefault = true, isActivateDa = false }: ForgotPasswordProps) {
    const [mode, setMode] = useState<SelectCompanyMode>("explanation")
    const companies = useSelector(selectCompanies)
    const reason = useSelector(selectCompanyReason)
    const username = useSelector(selectUsername)
    const duplicateValue = useSelector(selectCompanyDuplicateValue) ?? ""
    const { classes } = useStyles()
    const { mutateAsync: callCreatePassword } = usePublicApiMutation(selectCompany, {
        async onSuccess(_, data) {
            handleAuthResponse(data)
        },
    })
    const { handleAuthResponse } = useLoginFlow(isDefault, isActivateDa)
    const { l10n } = useAppLocalization()

    const definiteReason = reason ?? SelectCompanyReason.MULTIPLE_COMPANIES_FOR_DOMAIN
    const isAccountSelection = definiteReason === SelectCompanyReason.MULTIPLE_ACCOUNTS_FOR_USERNAME

    const validationSchema = yup.object<FormValues>({
        selectedCompany: yup.string().required(l10n.getString("authenticate-select-company-form-error-message")),
    })

    const groupedCompanies = useMemo(() => {
        if (isAccountSelection && companies && companies.length > 0) {
            const grouped = groupBy(
                companies,
                (choice) => choice.uid ?? l10n.getString("authenticate-select-company-company-group-unknown-uid"),
            )
            return Object.entries(grouped).map(([uid, accounts]) => ({
                companyUid: uid,
                companyName:
                    accounts[0]?.name ?? l10n.getString("authenticate-select-company-company-group-unknown-name"),
                companyAddress:
                    `${accounts[0].address?.streetName ?? ""} ${accounts[0].address?.streetNumber ?? ""}`.trim() ??
                    l10n.getString("authenticate-select-company-company-group-unknown-address"),
                accounts,
            }))
        }
        return null
    }, [isAccountSelection, companies, l10n])

    const onSubmit = async ({ selectedCompany }: FormValues) => {
        const selectedAccount = companies?.find((c) => c.uid === selectedCompany)

        if (selectedAccount && username) {
            const company: CompanyIdChoice = {
                uid: selectedAccount.uid,
                location: selectedAccount.location
                    ? {
                          id: selectedAccount.location.id,
                      }
                    : undefined,
                userGroup: selectedAccount.user?.userGroup
                    ? {
                          id: selectedAccount.user.userGroup.id,
                      }
                    : undefined,
                user: {
                    username,
                    ...(selectedAccount.user?.id ? { id: selectedAccount.user.id } : {}),
                },
            }

            await callCreatePassword({
                company,
            })
        }
    }

    const handleChangeList = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        setMode("list")
    }

    return (
        <Formik<FormValues> initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
            {({ errors, handleSubmit }) => (
                <Box component={Form} onSubmit={mode === "explanation" ? handleChangeList : handleSubmit}>
                    <LocalizedEnum base="authenticate-select-company-header-reason" enumValue={definiteReason}>
                        <Typography variant="h5" className={classes.header}>
                            Vælg firma
                        </Typography>
                    </LocalizedEnum>

                    {mode === "explanation" ? (
                        <LocalizedEnum
                            base="authenticate-select-company-reason-message"
                            enumValue={definiteReason}
                            vars={{ duplicateValue }}
                            elems={{ duplicateValue: <span className={classes.duplicateValue}></span> }}
                        >
                            <Typography>
                                Vi kunne ikke automatisk finde dit firma / enhed, da dit email domæne facilitynet.dk er
                                tilknyttet flere enheder.
                            </Typography>
                        </LocalizedEnum>
                    ) : (
                        <>
                            <LocalizedEnum base="authenticate-select-company-action-message" enumValue={definiteReason}>
                                <Typography variant="body1" className={classes.formPara}>
                                    Vælg firma / lokation, som du ønsker at logge ind hos:
                                </Typography>
                            </LocalizedEnum>

                            <List className={classes.companyList}>
                                {isAccountSelection && groupedCompanies && groupedCompanies.length > 0 ? (
                                    groupedCompanies.map((group) => (
                                        <div className={classes.companiesWrapper} key={group.companyUid}>
                                            <Typography variant="body1">{group.companyName}</Typography>
                                            <Typography color="textSecondary">{group.companyAddress}</Typography>
                                            {group.accounts.map((choice, index) => (
                                                <div key={index}>
                                                    <FormikRadioListItem name="selectedCompany" value={choice.uid}>
                                                        <Box>
                                                            <Typography variant="body1">
                                                                {choice?.user?.displayName}
                                                            </Typography>
                                                            <Typography variant="body2">
                                                                {choice?.user?.username}
                                                            </Typography>
                                                        </Box>
                                                    </FormikRadioListItem>
                                                </div>
                                            ))}
                                        </div>
                                    ))
                                ) : companies && companies.length > 0 ? (
                                    companies.map((choice, index) => (
                                        <Box key={index}>
                                            <FormikRadioListItem name="selectedCompany" value={choice.uid}>
                                                {choice?.name}
                                                {choice?.address && ` - ${choice.address.streetName}`}
                                            </FormikRadioListItem>
                                        </Box>
                                    ))
                                ) : (
                                    <LocalizedStrict id="authenticate-select-company-no-companies-available">
                                        <Typography color="textSecondary">No companies available</Typography>
                                    </LocalizedStrict>
                                )}
                            </List>
                            {errors && (
                                <ErrorMessage name="selectedCompany" component="div" className={classes.errorMessage} />
                            )}
                        </>
                    )}

                    <LocalizedStrict id="authenticate-select-company-submit-button" attrs={{ loadingLabel: true }}>
                        <FormikSubmitButton
                            variant="contained"
                            className={classes.submitButton}
                            loadingLabel="Gemmer..."
                        >
                            Next
                        </FormikSubmitButton>
                    </LocalizedStrict>
                </Box>
            )}
        </Formik>
    )
}
